import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Alert, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import formService from '../../services/formService';
import pdfService from '../../services/pdfService';
import api from '../../services/api';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvedForms, setApprovedForms] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({
    pendingApproval: 0,
    approved: 0,
    totalGenerated: 0
  });
  const [loading, setLoading] = useState(true);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedClientEmail, setSelectedClientEmail] = useState('');
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [previewDocxFilename, setPreviewDocxFilename] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showInspectorsModal, setShowInspectorsModal] = useState(false);
  const [inspectors, setInspectors] = useState([]);
  const [loadingInspectors, setLoadingInspectors] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Payment Info modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Email Certificates to Client modal state
  const [showEmailCertModal, setShowEmailCertModal] = useState(false);
  const [emailCertWorkOrder, setEmailCertWorkOrder] = useState('');
  const [emailCertSearchLoading, setEmailCertSearchLoading] = useState(false);
  const [emailCertResults, setEmailCertResults] = useState(null); // null = not searched yet
  const [emailCertSelected, setEmailCertSelected] = useState(new Set());
  const [emailCertClients, setEmailCertClients] = useState([]);
  const [emailCertClientId, setEmailCertClientId] = useState('');
  const [emailCertSending, setEmailCertSending] = useState(false);
  const [emailCertMessage, setEmailCertMessage] = useState(null); // {type, text}
  const [paymentForm, setPaymentForm] = useState({
    work_order_no: '',
    form_type_code: '',
    equipment_type_code: '',
    certificate_number: '',
    payment_status: 'paid',
    notes: ''
  });
  const [paymentEquipmentTypes, setPaymentEquipmentTypes] = useState([]);
  const [paymentEquipLoading, setPaymentEquipLoading] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentResult, setPaymentResult] = useState(null);

  const {
    page: pendingPage,
    setPage: setPendingPage,
    totalPages: pendingTotalPages,
    paginatedItems: paginatedPendingApprovals
  } = usePagination(pendingApprovals);

  const {
    page: approvedPage,
    setPage: setApprovedPage,
    totalPages: approvedTotalPages,
    paginatedItems: paginatedApprovedForms
  } = usePagination(approvedForms);

  const {
    page: inspectorsPage,
    setPage: setInspectorsPage,
    totalPages: inspectorsTotalPages,
    paginatedItems: paginatedInspectors
  } = usePagination(inspectors);

  const {
    page: emailCertPage,
    setPage: setEmailCertPage,
    totalPages: emailCertTotalPages,
    paginatedItems: paginatedEmailCertResults
  } = usePagination(emailCertResults || []);

  useEffect(() => {
    fetchSubmissionsAndClients();
  }, []);

  const fetchSubmissionsAndClients = async () => {
    try {
      setLoading(true);

      const [submissionsResponse, clientsResponse] = await Promise.all([
        formService.getSubmissions(),
        formService.getClients()
      ]);

      if (submissionsResponse.success) {
        const allSubmissions = submissionsResponse.submissions;

        // Separate pending approvals and approved forms
        const pending = allSubmissions.filter(s => s.current_status === 'reviewed_by_senior');
        const approved = allSubmissions.filter(s => s.current_status === 'approved_by_admin');

        const pendingApprovalsData = pending.map(submission => ({
          id: submission.submission_id,
          form_number: submission.form_number,
          work_order_number: submission.work_order_number,
          client_name: submission.client_name,
          client_email: submission.client_email,
          equipment_type: submission.equipment_type,
          inspection_date: submission.inspection_date,
          submitted_by: submission.submitted_by_name,
          reviewed_by: submission.latest_changed_by || 'Reviewer',
          status: submission.current_status,
          reviewed_at: submission.updated_at,
          certificate_number: submission.certificate_number || ''
        }));

        const approvedFormsData = approved.map(submission => ({
          id: submission.submission_id,
          form_number: submission.form_number,
          work_order_number: submission.work_order_number,
          client_name: submission.client_name,
          equipment_type: submission.equipment_type,
          inspection_date: submission.inspection_date,
          approved_at: submission.updated_at,
          pdf_sent: true, // TODO: Track this in database
          status: submission.current_status,
          certificate_number: submission.certificate_number || ''
        }));

        setPendingApprovals(pendingApprovalsData);
        setApprovedForms(approvedFormsData);

        setStats({
          pendingApproval: pendingApprovalsData.length,
          approved: approvedFormsData.length,
          totalGenerated: approvedFormsData.length
        });
      }

      if (clientsResponse.success) {
        const clientsData = clientsResponse.clients.map(client => ({
          id: client.client_id,
          name: client.client_name,
          email: client.contact_email
        }));
        setClients(clientsData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'reviewed_by_senior': <Badge bg="warning" text="dark">Pending Approval</Badge>,
      'approved_by_admin': <Badge bg="success">Approved</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const handlePreviewPDF = async (submission) => {
    try {
      setSelectedSubmission(submission);
      setShowPDFModal(true);
      setLoadingPDF(true);
      setPreviewDocxFilename('');

      // Generate DOCX preview (with seal and signatures automatically included)
      const response = await pdfService.previewCertificate(submission.id);

      if (response.success) {
        setPreviewDocxFilename(response.docx_filename);
      } else {
        alert('Error generating certificate preview: ' + response.message);
        setShowPDFModal(false);
      }
    } catch (error) {
      console.error('Error loading certificate preview:', error);
      alert('Failed to load certificate preview. Please try again.');
      setShowPDFModal(false);
    } finally {
      setLoadingPDF(false);
    }
  };

  const handleDownloadPreview = async () => {
    if (!previewDocxFilename) return;

    try {
      // Download the preview DOCX file with JWT token
      const blob = await pdfService.downloadPreview(previewDocxFilename);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = previewDocxFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading preview:', error);
      alert('Failed to download preview. Please try again.');
    }
  };

  const handleViewDataOnly = async (submission) => {
    try {
      setLoadingPDF(true);

      // Generate data-only preview (without seal and signatures)
      const response = await pdfService.dataOnlyPreview(submission.id);

      if (response.success) {
        // Download the data-only preview immediately
        const blob = await pdfService.downloadPreview(response.docx_filename);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.docx_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert('Data-only preview downloaded successfully! (No seal or signatures included)');
      } else {
        alert('Error generating data-only preview: ' + response.message);
      }
    } catch (error) {
      console.error('Error generating data-only preview:', error);
      alert('Failed to generate data-only preview. Please try again.');
    } finally {
      setLoadingPDF(false);
    }
  };

  const handleApproveClick = (submission) => {
    setSelectedSubmission(submission);
    setSelectedClientEmail(submission.client_email || '');
    setShowApprovalModal(true);
  };

  const handleSendEmail = async () => {
    if (!selectedClientEmail) {
      alert('Please select a client email address');
      return;
    }

    try {
      setSendingEmail(true);

      // First, approve the submission (update status to approved_by_admin)
      // This will also check payment status
      const approveResponse = await formService.approveSubmission(
        selectedSubmission.id,
        selectedClientEmail
      );

      if (approveResponse.success) {
        // Payment confirmed and form approved - proceed with certificate generation
        // Then, send the certificate via email with PDF attachment
        const emailResponse = await pdfService.sendCertificateEmail(
          selectedSubmission.id,
          selectedClientEmail
        );

        if (emailResponse.success) {
          // Close modals
          setShowApprovalModal(false);
          setShowPDFModal(false);

          // Refresh data
          await fetchSubmissionsAndClients();

          // Show success message
          alert('Form approved successfully! Certificate has been generated and sent to Admin and Inspector.');
        } else {
          alert('Form approved, but failed to send certificate email: ' + emailResponse.message);
        }
      }
    } catch (error) {
      console.error('Error approving and sending certificate:', error);

      // Check if it's a payment pending error
      const errorData = error.response?.data;
      if (errorData && errorData.payment_pending) {
        // Payment is pending - show specific message
        alert(
          `⚠️ Payment Pending\n\n` +
          `Work Order: ${errorData.work_order_no}\n` +
          `Equipment: ${errorData.equipment_type}\n\n` +
          `Payment reminder email has been sent to the client.\n` +
          `Certificate will be generated and sent after payment confirmation.\n\n` +
          `Status remains as "Reviewed by Reviewer" - you can retry after payment is confirmed.`
        );

        // Close modals
        setShowApprovalModal(false);
        setShowPDFModal(false);
      } else {
        // Other errors
        const errorMessage = errorData?.message || error.message || 'Unknown error';
        alert('Failed to approve form and send certificate: ' + errorMessage);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleViewDetails = (submissionId) => {
    navigate(`/submissions/${submissionId}`);
  };

  const handleOpenInspectorsModal = async () => {
    setShowInspectorsModal(true);
    setShowPasswords(false);
    if (inspectors.length === 0) {
      try {
        setLoadingInspectors(true);
        const response = await api.get('/api/auth/admin/inspectors');
        if (response.data.success) {
          setInspectors(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching inspectors:', error);
      } finally {
        setLoadingInspectors(false);
      }
    }
  };

  const handleOpenPaymentModal = () => {
    setPaymentForm({
      work_order_no: '',
      form_type_code: '',
      equipment_type_code: '',
      certificate_number: '',
      payment_status: 'paid',
      notes: ''
    });
    setPaymentEquipmentTypes([]);
    setPaymentErrors({});
    setPaymentResult(null);
    setShowPaymentModal(true);
  };

  const handlePaymentFormChange = async (field, value) => {
    const updated = { ...paymentForm, [field]: value };

    // Reset equipment type when form type changes
    if (field === 'form_type_code') {
      updated.equipment_type_code = '';
      setPaymentEquipmentTypes([]);
      if (value) {
        try {
          setPaymentEquipLoading(true);
          const res = await formService.getEquipmentTypesByCode(value);
          if (res.success) setPaymentEquipmentTypes(res.equipment_types);
        } catch {
          // silently ignore
        } finally {
          setPaymentEquipLoading(false);
        }
      }
    }

    // Clear field error on change
    if (paymentErrors[field]) {
      setPaymentErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }

    setPaymentForm(updated);
  };

  const handlePaymentSubmit = async () => {
    setPaymentErrors({});
    setPaymentResult(null);
    try {
      setPaymentSubmitting(true);
      const res = await formService.upsertPayment(paymentForm);
      setPaymentResult({ type: 'success', message: res.message, action: res.action });
      // Auto-close after 2 seconds on success
      setTimeout(() => setShowPaymentModal(false), 2000);
    } catch (err) {
      if (err.errors) {
        setPaymentErrors(err.errors);
      } else {
        setPaymentResult({ type: 'error', message: err.message || 'An error occurred. Please try again.' });
      }
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // ── Email Certificates to Client handlers ──────────────────────────────────
  const handleOpenEmailCertModal = async () => {
    setEmailCertWorkOrder('');
    setEmailCertResults(null);
    setEmailCertSelected(new Set());
    setEmailCertClientId('');
    setEmailCertMessage(null);
    setShowEmailCertModal(true);
    if (emailCertClients.length === 0) {
      try {
        const res = await formService.getClientsList();
        if (res.success) setEmailCertClients(res.clients);
      } catch { /* silently ignore */ }
    }
  };

  const handleEmailCertSearch = async () => {
    if (!emailCertWorkOrder.trim()) return;
    setEmailCertSearchLoading(true);
    setEmailCertResults(null);
    setEmailCertSelected(new Set());
    setEmailCertMessage(null);
    setEmailCertPage(1);
    try {
      const res = await formService.getCertificatesByWorkOrder(emailCertWorkOrder.trim());
      if (res.success) {
        setEmailCertResults(res.certificates);
      } else {
        setEmailCertMessage({ type: 'danger', text: res.message || 'Search failed.' });
      }
    } catch (err) {
      setEmailCertMessage({ type: 'danger', text: err.message || 'Search failed.' });
    } finally {
      setEmailCertSearchLoading(false);
    }
  };

  const handleEmailCertToggle = (id) => {
    setEmailCertSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleEmailCertSelectAll = () => {
    if (!emailCertResults) return;
    const selectable = emailCertResults
      .filter(c => c.payment_status === 'paid' && c.pdf_exists)
      .map(c => c.submission_id);
    setEmailCertSelected(new Set(selectable));
  };

  const handleEmailCertSend = async () => {
    if (emailCertSelected.size === 0 || !emailCertClientId) return;
    setEmailCertSending(true);
    setEmailCertMessage(null);
    try {
      const res = await formService.emailCertificatesToClient(
        Array.from(emailCertSelected),
        parseInt(emailCertClientId)
      );
      if (res.success) {
        setEmailCertMessage({ type: 'success', text: res.message });
        setEmailCertSelected(new Set());
      } else {
        setEmailCertMessage({ type: 'danger', text: res.message || 'Failed to send.' });
      }
    } catch (err) {
      setEmailCertMessage({ type: 'danger', text: err.message || 'Failed to send.' });
    } finally {
      setEmailCertSending(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center" style={{ paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container fluid className="mt-4 mb-5" style={{ paddingTop: '40px' }}>
        {/* Welcome Section */}
        <Row className="mb-4 align-items-start">
          <Col md="auto" className="d-flex align-items-start">
            <Button
              variant="dark"
              size="lg"
              onClick={() => navigate('/admin/employees-clients')}
              className="shadow-sm"
            >
              <i className="fas fa-id-badge me-2"></i>
              Manage Employees & Clients
            </Button>
          </Col>
          <Col className="text-center">
            <h2 className="fw-bold text-primary">
              <i className="fas fa-user-shield me-2"></i>
              Administrator Dashboard
            </h2>
            <p className="text-muted">
              Welcome back, <strong>{user?.full_name}</strong>! ({user?.emp_code})
            </p>
          </Col>
          <Col className="text-end d-flex gap-2 justify-content-end align-items-start flex-wrap">
            <Button
              variant="secondary"
              size="lg"
              onClick={handleOpenInspectorsModal}
              className="shadow-sm"
            >
              <i className="fas fa-users me-2"></i>
              View Inspectors Info
            </Button>
            <Button
              variant="success"
              size="lg"
              onClick={handleOpenPaymentModal}
              className="shadow-sm"
            >
              <i className="fas fa-credit-card me-2"></i>
              Update Payment Info
            </Button>
            <Button
              variant="warning"
              size="lg"
              onClick={handleOpenEmailCertModal}
              className="shadow-sm"
            >
              <i className="fas fa-paper-plane me-2"></i>
              Email Certificates to Client
            </Button>
            <Button
              variant="info"
              size="lg"
              onClick={() => navigate('/reports')}
              className="shadow-sm"
            >
              <i className="fas fa-chart-bar me-2"></i>
              View Reports & Charts
            </Button>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={6} lg={4} className="mb-3">
            <Card className="text-center shadow-sm h-100 border-warning">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-clock text-warning" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.pendingApproval}</h3>
                <p className="text-muted mb-0">Pending Approval</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={4} className="mb-3">
            <Card className="text-center shadow-sm h-100 border-success">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.approved}</h3>
                <p className="text-muted mb-0">Approved Forms</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={4} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-file-pdf text-danger" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.totalGenerated}</h3>
                <p className="text-muted mb-0">Certificates Generated</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Pending Approvals Section */}
        <Row className="mb-4">
          <Col>
            <Card className="shadow">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="fas fa-hourglass-half me-2"></i>
                  Forms Pending Final Approval
                </h5>
              </Card.Header>
              <Card.Body>
                {pendingApprovals.length === 0 ? (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    No forms pending approval at the moment.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Form Number</th>
                          <th>Work Order No</th>
                          <th>Client</th>
                          <th>Equipment</th>
                          <th>Inspection Date</th>
                          <th>Submitted By</th>
                          <th>Reviewed By</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPendingApprovals.map((submission) => (
                          <tr key={submission.id}>
                            <td>
                              <div className="fw-bold">{submission.form_number}</div>
                              {submission.certificate_number && (
                                <div className="text-muted small mt-1">
                                  <i className="fas fa-certificate me-1" style={{ fontSize: '0.7rem' }}></i>
                                  {submission.certificate_number}
                                </div>
                              )}
                            </td>
                            <td>{submission.work_order_number || '-'}</td>
                            <td>{submission.client_name}</td>
                            <td>{submission.equipment_type}</td>
                            <td>{new Date(submission.inspection_date).toLocaleDateString()}</td>
                            <td>{submission.submitted_by}</td>
                            <td>{submission.reviewed_by}</td>
                            <td>{getStatusBadge(submission.status)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2 mb-1"
                                onClick={() => handleViewDetails(submission.id)}
                              >
                                <i className="fas fa-eye me-1"></i>
                                View
                              </Button>
                              <Button
                                variant="info"
                                size="sm"
                                className="me-2 mb-1"
                                onClick={() => handleViewDataOnly(submission)}
                                disabled={loadingPDF}
                              >
                                <i className="fas fa-file-word me-1"></i>
                                Data Only
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                className="mb-1"
                                onClick={() => handlePreviewPDF(submission)}
                              >
                                <i className="fas fa-file-pdf me-1"></i>
                                Preview Certificate
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <TablePagination currentPage={pendingPage} totalPages={pendingTotalPages} onPageChange={setPendingPage} />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Approved Forms Section */}
        <Row>
          <Col>
            <Card className="shadow">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-check-double me-2"></i>
                  Approved Certificates
                </h5>
              </Card.Header>
              <Card.Body>
                {approvedForms.length === 0 ? (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    No approved certificates yet.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Form Number</th>
                          <th>Work Order No</th>
                          <th>Client</th>
                          <th>Equipment</th>
                          <th>Inspection Date</th>
                          <th>Approved At</th>
                          <th>Email Sent</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedApprovedForms.map((submission) => (
                          <tr key={submission.id}>
                            <td>
                              <div className="fw-bold">{submission.form_number}</div>
                              {submission.certificate_number && (
                                <div className="text-muted small mt-1">
                                  <i className="fas fa-certificate me-1" style={{ fontSize: '0.7rem' }}></i>
                                  {submission.certificate_number}
                                </div>
                              )}
                            </td>
                            <td>{submission.work_order_number || '-'}</td>
                            <td>{submission.client_name}</td>
                            <td>{submission.equipment_type}</td>
                            <td>{new Date(submission.inspection_date).toLocaleDateString()}</td>
                            <td>{new Date(submission.approved_at).toLocaleString()}</td>
                            <td>
                              {submission.pdf_sent ? (
                                <Badge bg="success">
                                  <i className="fas fa-check me-1"></i>
                                  Sent
                                </Badge>
                              ) : (
                                <Badge bg="secondary">Not Sent</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDetails(submission.id)}
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <TablePagination currentPage={approvedPage} totalPages={approvedTotalPages} onPageChange={setApprovedPage} />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* PDF Preview Modal */}
      <Modal show={showPDFModal} onHide={() => setShowPDFModal(false)} size="xl" fullscreen>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-file-pdf me-2"></i>
            Certificate Preview: {selectedSubmission?.form_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <>
              <Alert variant="info">
                <strong>Client:</strong> {selectedSubmission.client_name}<br />
                <strong>Equipment:</strong> {selectedSubmission.equipment_type}<br />
                <strong>Inspection Date:</strong> {new Date(selectedSubmission.inspection_date).toLocaleDateString()}
              </Alert>

              <Alert variant="success" className="mb-4">
                <i className="fas fa-check-circle me-2"></i>
                <strong>Certificate Ready!</strong> The certificate has been generated with company seal and inspector signatures automatically included.
              </Alert>

              {/* Certificate Preview/Download Area */}
              {loadingPDF ? (
                <div
                  style={{
                    border: '2px solid #dee2e6',
                    minHeight: '600px',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <h4 className="mt-3">Generating Certificate Preview...</h4>
                    <p className="text-muted">Including seal and signatures...</p>
                  </div>
                </div>
              ) : previewDocxFilename ? (
                <div
                  style={{
                    border: '2px solid #28a745',
                    backgroundColor: '#d4edda',
                    padding: '40px',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <i className="fas fa-file-word fa-5x text-success mb-3"></i>
                  <h4 className="mb-3">Certificate Preview Ready</h4>
                  <p className="text-muted mb-4">
                    The certificate has been generated with all seals, stamps, and signatures.<br />
                    Download the Word document to review the final certificate.
                  </p>
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleDownloadPreview}
                  >
                    <i className="fas fa-download me-2"></i>
                    Download Preview (Word Document)
                  </Button>
                  <p className="text-muted mt-3 small">
                    <i className="fas fa-info-circle me-1"></i>
                    Open the file in Microsoft Word to view the complete certificate
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    border: '2px solid #dee2e6',
                    minHeight: '400px',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <p className="text-muted">Preview will appear here...</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPDFModal(false)}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => {
              setShowPDFModal(false);
              handleApproveClick(selectedSubmission);
            }}
            disabled={!previewDocxFilename}
          >
            <i className="fas fa-paper-plane me-2"></i>
            Approve and Send Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approval and Email Modal */}
      <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)}>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fas fa-envelope me-2"></i>
            Approve and Send Certificate
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <>
              <Alert variant="info">
                <strong>Form Number:</strong> {selectedSubmission.form_number}<br />
                <strong>Client:</strong> {selectedSubmission.client_name}<br />
                <strong>Equipment:</strong> {selectedSubmission.equipment_type}
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Client Email <span className="text-danger">*</span>
                    <small className="text-muted fw-normal ms-2">(for payment reminder only)</small>
                  </Form.Label>
                  <Form.Select
                    value={selectedClientEmail}
                    onChange={(e) => setSelectedClientEmail(e.target.value)}
                    required
                  >
                    <option value="">-- Select Client Email --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.email}>
                        {client.name} ({client.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Alert variant="secondary">
                  <strong>Certificate Email Recipients:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>To:</strong> Admin — {user?.email}</li>
                    <li><strong>CC:</strong> Inspector — {selectedSubmission.submitted_by}</li>
                  </ul>
                </Alert>

                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  The PDF certificate will be sent to <strong>Admin and Inspector only</strong>.
                  To send certificates to the client, use the <strong>"Email Certificates to Client"</strong> button after approval.
                </Alert>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApprovalModal(false)}
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSendEmail}
            disabled={!selectedClientEmail || sendingEmail}
          >
            {sendingEmail ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Approve and Send Email
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update Payment Info Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)' }} className="text-white">
          <Modal.Title>
            <i className="fas fa-credit-card me-2"></i>
            Update Payment Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="info" className="mb-4 py-2">
            <i className="fas fa-info-circle me-2"></i>
            Enter the 5 fields below to add or update a payment record. If a record with the same Work Order + Form Type + Equipment + Certificate already exists, it will be updated.
          </Alert>

          {paymentResult && (
            <Alert
              variant={paymentResult.type === 'success' ? 'success' : 'danger'}
              className="mb-4"
            >
              {paymentResult.type === 'success' ? (
                <>
                  <i className="fas fa-check-circle me-2"></i>
                  <strong>{paymentResult.action === 'created' ? 'New record created! ' : 'Record updated! '}</strong>
                  {paymentResult.message}
                </>
              ) : (
                <>
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {paymentResult.message}
                </>
              )}
            </Alert>
          )}

          <Form>
            {/* Row 1: Work Order No + Certificate Number */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Work Order No <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 2007-4"
                    value={paymentForm.work_order_no}
                    onChange={e => handlePaymentFormChange('work_order_no', e.target.value)}
                    isInvalid={!!paymentErrors.work_order_no}
                  />
                  <Form.Control.Feedback type="invalid">
                    {paymentErrors.work_order_no}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Must match a work order in the system</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Certificate Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. SN-12345"
                    value={paymentForm.certificate_number}
                    onChange={e => handlePaymentFormChange('certificate_number', e.target.value)}
                    isInvalid={!!paymentErrors.certificate_number}
                  />
                  <Form.Control.Feedback type="invalid">
                    {paymentErrors.certificate_number}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">As shown under the form number in the dashboard</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: Form Type Code + Equipment Type Code */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Form Type Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={paymentForm.form_type_code}
                    onChange={e => handlePaymentFormChange('form_type_code', e.target.value)}
                    isInvalid={!!paymentErrors.form_type_code}
                  >
                    <option value="">-- Select Form Type --</option>
                    <option value="ADN">ADN (ADNOC)</option>
                    <option value="ENA">ENA (ENAS)</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {paymentErrors.form_type_code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Equipment Type Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={paymentForm.equipment_type_code}
                    onChange={e => handlePaymentFormChange('equipment_type_code', e.target.value)}
                    disabled={!paymentForm.form_type_code || paymentEquipLoading}
                    isInvalid={!!paymentErrors.equipment_type_code}
                  >
                    <option value="">
                      {paymentEquipLoading
                        ? 'Loading...'
                        : !paymentForm.form_type_code
                        ? 'Select form type first'
                        : '-- Select Equipment Type --'}
                    </option>
                    {paymentEquipmentTypes.map(et => (
                      <option key={et.equipment_type_id} value={et.equipment_code}>
                        {et.equipment_code} — {et.equipment_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {paymentErrors.equipment_type_code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: Payment Status */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Payment Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={paymentForm.payment_status}
                    onChange={e => handlePaymentFormChange('payment_status', e.target.value)}
                    isInvalid={!!paymentErrors.payment_status}
                    style={{
                      borderColor: paymentForm.payment_status === 'paid' ? '#198754' : '#fd7e14',
                      color: paymentForm.payment_status === 'paid' ? '#198754' : '#fd7e14',
                      fontWeight: '600'
                    }}
                  >
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {paymentErrors.payment_status}
                  </Form.Control.Feedback>
                  <Form.Text>
                    {paymentForm.payment_status === 'paid' ? (
                      <span className="text-success"><i className="fas fa-check-circle me-1"></i>Certificate will be sent on approval</span>
                    ) : (
                      <span className="text-warning"><i className="fas fa-exclamation-circle me-1"></i>Certificate will be held — payment reminder sent to client</span>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Notes <span className="text-muted fw-normal">(optional)</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Any remarks about this payment..."
                    value={paymentForm.notes}
                    onChange={e => handlePaymentFormChange('notes', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" onClick={() => setShowPaymentModal(false)} disabled={paymentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handlePaymentSubmit}
            disabled={paymentSubmitting || !!paymentResult?.type === 'success'}
          >
            {paymentSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Save Payment Record
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Certificates to Client Modal */}
      <Modal
        show={showEmailCertModal}
        onHide={() => setShowEmailCertModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <i className="fas fa-paper-plane me-2"></i>
            Email Certificates to Client
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Step 1: Search */}
          <Row className="mb-3 align-items-end">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-semibold">Work Order Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter work order number..."
                  value={emailCertWorkOrder}
                  onChange={e => setEmailCertWorkOrder(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleEmailCertSearch()}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                onClick={handleEmailCertSearch}
                disabled={emailCertSearchLoading || !emailCertWorkOrder.trim()}
                className="w-100"
              >
                {emailCertSearchLoading
                  ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Searching...</>
                  : <><i className="fas fa-search me-2"></i>Search</>}
              </Button>
            </Col>
          </Row>

          {emailCertMessage && (
            <Alert variant={emailCertMessage.type} dismissible onClose={() => setEmailCertMessage(null)}>
              {emailCertMessage.type === 'success'
                ? <><i className="fas fa-check-circle me-2"></i>{emailCertMessage.text}</>
                : <><i className="fas fa-exclamation-circle me-2"></i>{emailCertMessage.text}</>}
            </Alert>
          )}

          {/* Step 2: Results table */}
          {emailCertResults !== null && (
            emailCertResults.length === 0 ? (
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                No approved certificates found for work order <strong>{emailCertWorkOrder}</strong>.
              </Alert>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">
                    <strong>{emailCertResults.length}</strong> certificate(s) found for <strong>{emailCertWorkOrder}</strong>
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleEmailCertSelectAll}
                  >
                    <i className="fas fa-check-double me-1"></i>Select All Paid
                  </Button>
                </div>
                <div className="table-responsive mb-3">
                  <Table bordered hover size="sm" className="align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: 40 }}>Select</th>
                        <th>Form Number</th>
                        <th>Form Type</th>
                        <th>Equipment</th>
                        <th>Certificate No</th>
                        <th>Payment</th>
                        <th>PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmailCertResults.map(cert => {
                        const canSelect = cert.payment_status === 'paid' && cert.pdf_exists;
                        const isSelected = emailCertSelected.has(cert.submission_id);
                        return (
                          <tr key={cert.submission_id} className={isSelected ? 'table-warning' : ''}>
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                disabled={!canSelect}
                                onChange={() => handleEmailCertToggle(cert.submission_id)}
                                title={!cert.pdf_exists ? 'PDF not found on disk' : cert.payment_status !== 'paid' ? 'Payment not confirmed' : ''}
                              />
                            </td>
                            <td><strong>{cert.form_number}</strong></td>
                            <td><Badge bg="secondary">{cert.form_type_code}</Badge></td>
                            <td>{cert.equipment_name}</td>
                            <td><small className="text-muted">{cert.cert_no || '—'}</small></td>
                            <td>
                              {cert.payment_status === 'paid'
                                ? <Badge bg="success"><i className="fas fa-check me-1"></i>Paid</Badge>
                                : cert.payment_status === 'pending'
                                ? <Badge bg="danger">Pending</Badge>
                                : <Badge bg="secondary">Not Recorded</Badge>}
                            </td>
                            <td className="text-center">
                              {cert.pdf_exists
                                ? <i className="fas fa-file-pdf text-success" title="PDF available"></i>
                                : <i className="fas fa-times text-danger" title="PDF not found on disk"></i>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <TablePagination currentPage={emailCertPage} totalPages={emailCertTotalPages} onPageChange={setEmailCertPage} />
                </div>

                {/* Step 3: Client + Send */}
                {emailCertSelected.size > 0 && (
                  <div className="border rounded p-3 bg-light">
                    <Row className="align-items-end g-3">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Select Client <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={emailCertClientId}
                            onChange={e => setEmailCertClientId(e.target.value)}
                          >
                            <option value="">-- Select Client --</option>
                            {emailCertClients.map(c => (
                              <option key={c.client_id} value={c.client_id}>
                                {c.client_name} — {c.contact_email || 'no email'}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Button
                          variant="warning"
                          className="w-100 fw-bold"
                          onClick={handleEmailCertSend}
                          disabled={emailCertSending || !emailCertClientId}
                        >
                          {emailCertSending
                            ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Sending...</>
                            : <><i className="fas fa-paper-plane me-2"></i>Email {emailCertSelected.size} Certificate(s)</>}
                        </Button>
                      </Col>
                    </Row>
                    <div className="mt-2">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        {emailCertSelected.size} certificate(s) selected — all will be sent as attachments in a single email.
                      </small>
                    </div>
                  </div>
                )}
              </>
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailCertModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Inspectors Info Modal */}
      <Modal
        show={showInspectorsModal}
        onHide={() => setShowInspectorsModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-secondary text-white">
          <Modal.Title>
            <i className="fas fa-users me-2"></i>
            Inspector &amp; Admin Accounts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingInspectors ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="secondary" />
              <p className="mt-3 text-muted">Loading accounts...</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  {inspectors.length} account{inspectors.length !== 1 ? 's' : ''} found
                </span>
                <Button
                  variant={showPasswords ? 'outline-danger' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowPasswords(prev => !prev)}
                >
                  <i className={`fas fa-${showPasswords ? 'eye-slash' : 'eye'} me-1`}></i>
                  {showPasswords ? 'Hide Passwords' : 'Show Passwords'}
                </Button>
              </div>
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Emp Code</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Role</th>
                      <th>Email Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInspectors.map(u => (
                      <tr key={u.userid}>
                        <td><strong>{u.emp_code}</strong></td>
                        <td>{u.full_name}</td>
                        <td>{u.email}</td>
                        <td>
                          {u.role === 'admin' ? (
                            <span className="text-muted fst-italic">— Hidden (Admin)</span>
                          ) : showPasswords ? (
                            u.plain_password === 'Not available'
                              ? <span className="text-muted fst-italic">Not available (pre-existing account)</span>
                              : <code>{u.plain_password}</code>
                          ) : (
                            <span className="text-muted">••••••••</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={
                            u.role === 'admin' ? 'danger' :
                            u.role === 'sr_inspector' ? 'warning' : 'primary'
                          } text={u.role === 'sr_inspector' ? 'dark' : undefined}>
                            {u.role === 'jr_inspector' ? 'Inspector' :
                             u.role === 'sr_inspector' ? 'Reviewer' : 'Admin'}
                          </Badge>
                        </td>
                        <td className="text-center">
                          {u.email_verified
                            ? <Badge bg="success"><i className="fas fa-check me-1"></i>Verified</Badge>
                            : <Badge bg="danger"><i className="fas fa-times me-1"></i>Not Verified</Badge>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <TablePagination currentPage={inspectorsPage} totalPages={inspectorsTotalPages} onPageChange={setInspectorsPage} />
              </div>
              {inspectors.some(u => u.plain_password === 'Not available') && (
                <Alert variant="warning" className="mt-3 mb-0 small">
                  <i className="fas fa-info-circle me-1"></i>
                  <strong>Note:</strong> Accounts showing "Not available" were created before this feature was added. Their passwords are stored as a one-way hash and cannot be recovered. Ask those users to change their password — the new password will be captured and shown here.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInspectorsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminDashboard;
